import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Upload, 
  X, 
  File, 
  Image, 
  FileText, 
  Video, 
  Music,
  CheckCircle,
  AlertCircle,
  Loader,
  Download,
  Eye,
  Play
} from 'lucide-react';
import { uploadAPI } from '../../services/api';
import api from '../../services/api';
import { getImageUrl } from '../../utils/apiUtils';

const FileUpload = ({
  multiple = false,
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 5,
  onUploadComplete,
  onUploadError,
  uploadType = 'general', // general, image, document, avatar, gif, lottie
  className = '',
  compact = false,
  autoUpload = false
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const getFileIcon = (fileType, fileName) => {
    if (fileType.startsWith('image/')) {
      // Check for GIF specifically
      if (fileType === 'image/gif' || fileName.toLowerCase().endsWith('.gif')) {
        return Play; // Use play icon for GIFs to indicate animation
      }
      return Image;
    }
    if (fileType.startsWith('video/')) return Video;
    if (fileType.startsWith('audio/')) return Music;
    if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
    // Check for LottieFiles (JSON files that are typically Lottie animations)
    if (fileType === 'application/json' && fileName.toLowerCase().endsWith('.json')) {
      return Play; // Use play icon for LottieFiles
    }
    return File;
  };

  const getFileTypeLabel = (fileType, fileName) => {
    if (fileType === 'image/gif' || fileName.toLowerCase().endsWith('.gif')) {
      return 'GIF Animation';
    }
    if (fileType === 'application/json' && fileName.toLowerCase().endsWith('.json')) {
      return 'Lottie Animation';
    }
    if (fileType.startsWith('image/')) return 'Image';
    if (fileType.startsWith('video/')) return 'Video';
    if (fileType.startsWith('audio/')) return 'Audio';
    if (fileType.includes('pdf') || fileType.includes('document')) return 'Document';
    return 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    if (file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)}`;
    }
    
    if (accept !== '*/*') {
      const acceptedTypes = accept.split(',').map(type => type.trim());
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const mimeType = file.type;
      
      // Enhanced validation for GIFs and LottieFiles
      const isGif = fileExtension === '.gif' || mimeType === 'image/gif';
      const isLottie = fileExtension === '.json' && mimeType === 'application/json';
      
      const isAccepted = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type;
        }
        if (type.includes('/*')) {
          return mimeType.startsWith(type.replace('/*', ''));
        }
        // Special handling for GIFs and LottieFiles
        if (type === 'image/*' && isGif) {
          return true;
        }
        if (type === 'application/json' && isLottie) {
          return true;
        }
        return mimeType === type;
      });
      
      if (!isAccepted) {
        return `File type not accepted. Accepted types: ${accept}`;
      }
    }
    
    return null;
  };

  const handleFiles = useCallback((newFiles) => {
    console.log('🔍 handleFiles called with:', newFiles);
    console.log('🔍 autoUpload setting:', autoUpload);
    
    const fileArray = Array.from(newFiles);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push({ file: file.name, error });
      } else {
        validFiles.push({
          file,
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'pending',
          progress: 0,
          url: null,
          error: null
        });
      }
    });

    console.log('🔍 Valid files:', validFiles);
    console.log('🔍 Errors:', errors);

    if (errors.length > 0 && onUploadError) {
      onUploadError(errors);
    }

    if (validFiles.length > 0) {
      const totalFiles = files.length + validFiles.length;
      if (totalFiles > maxFiles) {
        const allowedFiles = validFiles.slice(0, maxFiles - files.length);
        setFiles(prev => [...prev, ...allowedFiles]);
        if (onUploadError) {
          onUploadError([{ error: `Maximum ${maxFiles} files allowed` }]);
        }
      } else {
        setFiles(prev => [...prev, ...validFiles]);
        
        // Auto-upload if enabled - we'll handle this after uploadFiles is defined
        if (autoUpload) {
          console.log('🔍 Auto-upload enabled, will schedule upload after state update');
        } else {
          console.log('🔍 Auto-upload disabled, manual upload required');
        }
      }
    }
  }, [files, maxFiles, maxSize, accept, onUploadError, autoUpload]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e) => {
    console.log('🔍 handleFileInput called');
    console.log('🔍 Files selected:', e.target.files);
    
    if (e.target.files && e.target.files[0]) {
      console.log('🔍 Calling handleFiles with selected files');
      handleFiles(e.target.files);
    } else {
      console.log('🔍 No files selected');
    }
  };

  // Define uploadFiles function before the useEffect that uses it
  const uploadFiles = useCallback(async () => {
    console.log('🔍 uploadFiles called');
    const pendingFiles = files.filter(f => f.status === 'pending');
    console.log('🔍 Pending files:', pendingFiles);
    
    if (pendingFiles.length === 0) {
      console.log('🔍 No pending files to upload');
      return;
    }

    console.log('🔍 Starting upload for', pendingFiles.length, 'files');
    setUploading(true);

    try {
      const completedFiles = [];
      
      for (const fileObj of pendingFiles) {
        const result = await uploadFile(fileObj);
        if (result && result.status === 'completed') {
          completedFiles.push(result);
        }
      }

      // Call completion callback with successful uploads
      console.log('🔍 Completed files:', completedFiles);
      console.log('🔍 onUploadComplete callback exists:', !!onUploadComplete);
      if (onUploadComplete && completedFiles.length > 0) {
        console.log('🔍 Calling onUploadComplete with:', completedFiles);
        onUploadComplete(completedFiles);
      }

    } catch (error) {
      console.error('Upload error:', error);
      if (onUploadError) {
        onUploadError([{ error: 'Upload failed' }]);
      }
    } finally {
      setUploading(false);
    }
  }, [files, onUploadComplete, onUploadError]);

  // Effect to handle auto-upload when files are added and autoUpload is enabled
  useEffect(() => {
    if (autoUpload && files.length > 0) {
      const pendingFiles = files.filter(f => f.status === 'pending');
      if (pendingFiles.length > 0) {
        console.log('🔍 Auto-upload triggered for', pendingFiles.length, 'files');
        // Use setTimeout to ensure state is fully updated
        setTimeout(() => {
          uploadFiles();
        }, 100);
      }
    }
  }, [files, autoUpload, uploadFiles]);

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadFile = async (fileObj) => {
    console.log('🔍 uploadFile called with:', fileObj);
    console.log('🔍 uploadType:', uploadType);
    
    setFiles(prev => prev.map(f => 
      f.id === fileObj.id 
        ? { ...f, status: 'uploading', progress: 0 }
        : f
    ));

    try {
      let response;
      
      // Choose appropriate upload endpoint based on type
      console.log('🔍 Choosing upload endpoint for type:', uploadType);
      switch (uploadType) {
        case 'image':
          response = await uploadAPI.uploadImage(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          break;
        case 'document':
          response = await uploadAPI.uploadDocument(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          break;
        case 'avatar':
          response = await uploadAPI.uploadAvatar(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          break;
        case 'animation':
          response = await uploadAPI.uploadAnimation(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          break;
        case 'product':
          console.log('🔍 Using product upload endpoint');
          response = await uploadAPI.uploadProduct(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          console.log('🔍 Product upload response:', response);
          break;
        case 'video':
          response = await uploadAPI.uploadVideo(fileObj.file, {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
          break;
        default:
          // For general uploads, create FormData
          const formData = new FormData();
          formData.append('file', fileObj.file);
          formData.append('type', uploadType);
          
          response = await api.post('/upload/file', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f => 
                f.id === fileObj.id 
                  ? { ...f, progress }
                  : f
              ));
            }
          });
      }

      // Update file with success status
      const constructedUrl = getImageUrl(response.data.data.url);
      console.log('🔍 Constructed image URL:', constructedUrl);
      console.log('🔍 Original URL from API:', response.data.data.url);
      console.log('🔍 Upload response success:', response.data.success);
      
      setFiles(prev => {
        const updatedFiles = prev.map(f => 
          f.id === fileObj.id 
            ? { 
                ...f, 
                status: 'completed', 
                progress: 100,
                url: constructedUrl,
                serverFile: response.data.data
              }
            : f
        );
        return updatedFiles;
      });

      // Call onUploadComplete callback
      if (onUploadComplete) {
        onUploadComplete([{
          id: fileObj.id,
          url: constructedUrl,
          filename: response.data.data.filename,
          originalName: response.data.data.originalName,
          size: response.data.data.size,
          fileType: response.data.data.fileType,
          storageType: response.data.data.storageType
        }]);
      }

      // Return the completed file object
      return {
        id: fileObj.id,
        url: constructedUrl,
        filename: response.data.data.filename,
        originalName: response.data.data.originalName,
        size: response.data.data.size,
        fileType: response.data.data.fileType,
        storageType: response.data.data.storageType,
        status: 'completed'
      };

    } catch (error) {
      console.error('Upload error:', error);
      
      setFiles(prev => prev.map(f => 
        f.id === fileObj.id 
          ? { ...f, status: 'error', error: error.response?.data?.message || 'Upload failed' }
          : f
      ));

      if (onUploadError) {
        onUploadError([{
          id: fileObj.id,
          filename: fileObj.file.name,
          error: error.response?.data?.message || 'Upload failed'
        }]);
      }
      
      return null;
    }
  };



  const clearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'completed'));
  };

  const retryFailed = () => {
    setFiles(prev => prev.map(f => 
      f.status === 'error' 
        ? { ...f, status: 'pending', error: null, progress: 0 }
        : f
    ));
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${
          compact ? 'p-3' : 'p-6'
        } text-center ${
          dragActive
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInput}
          onClick={() => console.log('🔍 File input clicked')}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />
        
        <div className={`relative z-0 ${compact ? 'space-y-1' : 'space-y-2'}`}>
          <Upload className={`mx-auto text-gray-400 ${compact ? 'h-8 w-8' : 'h-12 w-12'}`} />
          <div>
            <p className={`font-medium text-gray-900 dark:text-white ${compact ? 'text-sm' : 'text-lg'}`}>
              {compact ? 'Click to upload' : 'Drop files here or click to upload'}
            </p>
            {!compact && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {accept === '*/*' ? 'Any file type' : `Accepted: ${accept}`}
                  {accept.includes('image/*') && ' (including GIFs)'}
                  {accept.includes('application/json') && ' (including LottieFiles)'}
                </p>
                <p className="text-xs text-gray-400">
                  Max size: {formatFileSize(maxSize)} • Max files: {maxFiles}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Files ({files.length})
            </h4>
            <div className="flex space-x-2">
              {files.some(f => f.status === 'completed') && (
                <button
                  onClick={clearCompleted}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Clear completed
                </button>
              )}
              {files.some(f => f.status === 'error') && (
                <button
                  onClick={retryFailed}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Retry failed
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {files.map((fileObj) => {
              const FileIcon = getFileIcon(fileObj.type, fileObj.name);
              const FileTypeLabel = getFileTypeLabel(fileObj.type, fileObj.name);
              
              return (
                <div
                  key={fileObj.id}
                  className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    <FileIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {fileObj.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {FileTypeLabel}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(fileObj.size)}
                    </p>
                    
                    {/* Progress Bar */}
                    {fileObj.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${fileObj.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {fileObj.progress}% uploaded
                        </p>
                      </div>
                    )}
                    
                    {/* Error Message */}
                    {fileObj.status === 'error' && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {fileObj.error}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Status Icon */}
                    {fileObj.status === 'pending' && (
                      <div className="w-5 h-5 rounded-full bg-gray-300 dark:bg-gray-600" />
                    )}
                    {fileObj.status === 'uploading' && (
                      <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                    )}
                    {fileObj.status === 'completed' && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    {fileObj.status === 'error' && (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    
                    {/* Action Buttons */}
                    {fileObj.status === 'completed' && fileObj.url && (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => window.open(fileObj.url, '_blank')}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="View file"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <a
                          href={fileObj.url}
                          download={fileObj.name}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                    
                    {/* Remove Button */}
                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Remove file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Upload Button */}
          {!autoUpload && files.some(f => f.status === 'pending') && (
            <button
              onClick={uploadFiles}
              disabled={uploading}
              className="w-full btn-primary flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin h-4 w-4 mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Files
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
